import { useUserIdentityContext } from "@core/services/user-identity.context";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { usePrintableCard } from "@domain/customer/list-cards/printable-card.hook";
import { PersonFormRule, PersonFormSchema } from "@factory/form/form.rule";
import { useFormDefaultPerson } from "@factory/form/form-default-person.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import {
  GetCardSeriesExtendDto,
  GetCardSeriesModel,
  GetResponseCardSeriesModel,
} from "@factory/model/query-model-card-series";
import { GetResponsePersonModel } from "@factory/model/query-model-person";
import {
  GetResponseRewardModel,
  RewardBalance,
} from "@factory/model/query-model-reward";
import { ScanMethod } from "@factory/model/scan-method.enum";
import { useQyGetCardSeries } from "@factory/query/card-series.query";
import { useQyGetGoogleCloudAssetPreview } from "@factory/query/google-cloud-storage.query";
import {
  useQyBlockPerson,
  useQyDeletePerson,
  useQyGetPersonById,
  useQyUpdatePerson,
} from "@factory/query/person.query";
import { useQyGetRewardBalance } from "@factory/query/reward.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { useCallback, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditCustomer() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const { downloadPng, downloadPdf, cardElement } = usePrintableCard();
  const { tenant } = getUserRole();
  const { formatDateForInput } = useDateTimeContext();
  const { getDefault } = useFormDefaultPerson();
  const navigate = useNavigate();
  const { mapUpdatePerson, mapDeletePerson } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { personId } = useParams();
  const [avatar, setAvatar] = useState<string>("");
  const [cardAlias, setCardAlias] = useState("");
  const [, setSelectedCard] = useState<GetCardSeriesModel>();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Download PNG", type: "more", value: ActionEnum.DownloadPNG },
    { label: "Download PDF", type: "more", value: ActionEnum.DownloadPDF },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];
  const [displayMode, setDisplayMode] = useState<ScanMethod>(ScanMethod.QR);

  // QUERY DECLARATION
  // API GETBYID Person
  const handleGetApiSuccess = (data: GetResponsePersonModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    if (responseData?.avatar) {
      viewAsset(responseData?.avatar, {
        onSuccess(data) {
          const assetDisplay = URL.createObjectURL(data);
          setValue("photo", assetDisplay);
        },
      });
    }

    setValue("tenant", responseData?.tenant || "");
    setValue("role", responseData?.role || "");
    setValue("firstName", responseData?.first_name || "");
    setValue("middleName", responseData?.middle_name || "");
    setValue("lastName", responseData?.last_name || "");
    setValue("username", responseData?.username || "");
    setValue("email", responseData?.email || "");
    setValue("password", responseData?.password || "");
    setValue("isVerified", Boolean(responseData?.is_verified));
    setValue("avatar", responseData?.avatar || "");
    setValue("activationCode", responseData?.activation_code || "");
    setValue("gender", responseData?.gender || "");
    if (responseData?.birthday) {
      setValue(
        "birthday",
        formatDateForInput(responseData?.birthday) as any,
        undefined
      );
    }
    setValue("isBlocked", Boolean(responseData?.is_blocked));
    setValue("mobileNumber", responseData?.mobile_number || "");
    setValue("fatherName", responseData?.father_name || "");
    setValue("motherName", responseData?.mother_name || "");
    setValue(
      "emergencyContact_1Number",
      responseData?.emergency_contact_1_number || ""
    );
    setValue(
      "emergencyContact_1Name",
      responseData?.emergency_contact_1_name || ""
    );
    setValue(
      "emergencyContact_1Relationship",
      responseData?.emergency_contact_1_relationship || ""
    );
    setValue(
      "emergencyContact_2Number",
      responseData?.emergency_contact_2_number || ""
    );
    setValue(
      "emergencyContact_2Name",
      responseData?.emergency_contact_2_name || ""
    );
    setValue(
      "emergencyContact_2Relationship",
      responseData?.emergency_contact_2_relationship || ""
    );
    setValue("sss", responseData?.sss || "");
    setValue("philhealth", responseData?.philhealth || "");
    setValue("pagibig", responseData?.pagibig || "");
    setValue("tin", responseData?.tin || "");
    setValue("medicalCondition", responseData?.medical_condition || "");
    setValue("bloodType", responseData?.blood_type || "");
    setValue("elementary", responseData?.elementary || "");
    setValue("highSchool", responseData?.high_school || "");
    setValue("college", responseData?.college || "");
    setValue("postCollege", responseData?.post_college || "");
    setValue("undergraduate", responseData?.undergraduate || "");
    setValue("curriculumVitae", responseData?.curriculum_vitae || "");
    setValue("supportingDoc", responseData?.supporting_doc || "");
    setValue("intentLetter", responseData?.intent_letter || "");
    setValue("applicationLetter", responseData?.application_letter || "");
  };
  const {
    data: personResponse,
    isLoading,
    isError: personError,
  } = useQyGetPersonById(personId || "", handleGetApiSuccess);
  const personData = personResponse?.data?.[0];

  // API VIEW ASSET
  const { mutate: viewAsset } = useQyGetGoogleCloudAssetPreview();

  // API UPDATE Person
  const handleUpdateApiSuccess = () => {
    showSuccess("Customer updated successfully");
  };
  const { mutate: updatePerson, isLoading: isEditLoading } = useQyUpdatePerson(
    handleUpdateApiSuccess
  );

  // API DELETE Person
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Customer deleted successfully");
  };
  const { mutate: deletePerson } = useQyDeletePerson(handleDeleteApiSuccess);

  // API BLOCK Person
  const handleBlockApiSuccess = () => {
    handleBack();
    showSuccess("Customer blocked successfully");
  };
  const { mutate: blockPerson } = useQyBlockPerson(handleBlockApiSuccess);

  // API BALANCE
  const handleBalanceApiSuccess = (response: GetResponseRewardModel) => {
    const data = response as RewardBalance;
    if (data?.level_avatar) {
      viewAsset(data?.level_avatar, {
        onSuccess(data) {
          const assetDisplay = URL.createObjectURL(data);
          setAvatar(assetDisplay);
        },
      });
    }
    setValue("cardCategory", data.card_category || "");
  };
  const { data: balance } = useQyGetRewardBalance(
    cardAlias ?? "",
    tenant,
    !!cardAlias,
    handleBalanceApiSuccess
  );
  const balanceData = balance as RewardBalance;

  // API CARD SERIES
  const handleCardSeriesSuccess = (data: GetResponseCardSeriesModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }
    const responseData = data.data?.[0];
    setSelectedCard(responseData as GetCardSeriesExtendDto);
    setCardAlias(responseData?.card_alias ?? "");
  };
  useQyGetCardSeries(
    "",
    9999,
    0,
    {},
    { user_code: personId ?? "" },
    !!personId,
    handleCardSeriesSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<PersonFormSchema>({
    defaultValues: getDefault(personData),
    resolver: zodResolver(PersonFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: PersonFormSchema) => {
    if (!personData || !personData.code) {
      throw new Error("No person data");
    }

    const formData = mapUpdatePerson(form, personData.code);
    updatePerson(formData);
  };
  const handleValidateError = (err: FieldErrors<PersonFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!personData || !personData.code) {
      throw new Error("No person data found");
    }

    const form = mapDeletePerson(personData.code);
    deletePerson(form);
  };
  const blockAccept = () => {
    if (!personData || !personData.code) {
      throw new Error("No person data found");
    }

    const form = mapDeletePerson(personData.code);
    blockPerson(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.DownloadPNG:
        downloadPng(balanceData.card_alias);
        break;
      case ActionEnum.DownloadPDF:
        downloadPdf(balanceData.card_alias);
        break;
      case ActionEnum.Block:
        confirmDialog({
          message: "Are you sure you want to block this customer?",
          header: "Block Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept: blockAccept,
        });
        break;
      case ActionEnum.Unlock:
        navigate("unlock");
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this customer?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };
  const getActions = useCallback(() => {
    if (personData?.is_locked) {
      return [
        ...actions,
        { label: "Unlock", type: "more", value: ActionEnum.Unlock },
      ] as ButtonAction[];
    }

    return actions as ButtonAction[];
  }, [personData?.is_locked]);

  return {
    personId,
    personData,
    isLoading,
    personError,
    formMethod,
    actions,
    isEditLoading,
    balanceData,
    avatar,
    displayMode,
    setDisplayMode,
    cardElement,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
    getActions,
  };
}
